/**
 * Two rules, one test: a school with **no subscription at all**, or whose
 * subscriptions have **all expired**, may neither
 *
 *   * CREATE a new locale (or duplicate one, which makes a new locale), nor
 *   * EDIT the locales it already owns.
 *
 * Everything else stays open — viewing, the overview, locating on the map,
 * retrieving student answers, and deleting: the school still owns its data.
 *
 * This reads `subscriptionPlan` rather than counting subscriptions because the
 * two blocked states arrive as the SAME payload.
 * `get_privilege_detail_by_school_id` answers `subscription: "Premium"` only
 * when a plan is currently valid (`date_start <= CURDATE() <= date_expire`) and
 * `"Standard"` otherwise, and `get_subscription_list_by_school_id` — the only
 * other subscription data the composer holds — returns ONLY currently-valid
 * rows, so an expired grant and an absent one are indistinguishable from here.
 * `subscriptionPlan === 'Premium'` is the same "has an active subscription" test
 * TopBar's Extra Locale item and SubscriptionDialog's quota card already use.
 *
 * Three ways out, each deliberate:
 *
 * - **The master switch.** `Config.SUBSCRIPTION_FLAG` off means subscriptions
 *   are not enforced anywhere in this build — the create and duplicate quota
 *   checks short-circuit on it too — so this must not be the one thing that
 *   still bites.
 * - **The CLST admin.** `isAdminUser()` is school 3 + role 2. Without this,
 *   support could not open a lapsed school's locale to fix it, and a lapse on
 *   school 3's own Global Admin plan would lock the admins out of everything.
 * - **An unanswered `get_privilege_detail_by_school_id`.** `subscriptionPlan`
 *   initialises to `"Standard"` in UserDataProfile and stays there if that
 *   request fails, so trusting it alone would read a network error as "this
 *   school does not pay" and freeze a paying school out of its own editor.
 *   `subscriptionInfo` is the arrival signal: it starts `[]` and the response
 *   always carries privilege rows (the Standard branch returns the whole
 *   catalog). Unknown therefore means ALLOW — this gate is a UI affordance, the
 *   server is what actually holds the line, and failing open costs a write that
 *   should have been refused where failing closed costs a working school its
 *   editor.
 *
 * A plan granted for NEXT term reads as blocked here, which is the one state the
 * rules do not name. Nothing the composer fetches can see it (both endpoints
 * hide a not-yet-started row), and the same school is already treated as
 * unsubscribed by the quota card. Enforcement disagrees — `get_epack_quota`
 * leaves it unlimited until `date_start` — so before this gate such a school
 * could create freely; now it waits for its term to open. Distinguishing it
 * needs a new backend read.
 */
export function canWriteLocale({
  subscriptionFlag,
  isAdmin,
  subscriptionPlan,
  subscriptionInfo,
} = {}) {
  if (subscriptionFlag !== true) {
    return true;
  }
  if (isAdmin) {
    return true;
  }
  if (!Array.isArray(subscriptionInfo) || subscriptionInfo.length === 0) {
    return true;
  }
  return subscriptionPlan === 'Premium';
}

/**
 * "A lapsed school cannot edit its existing locales." Deliberately the same test
 * as below — aliased rather than reimplemented so the two rules cannot drift
 * apart by accident, and named so each call site reads as the rule it enforces.
 */
export const canEditExistingLocale = canWriteLocale;

/**
 * "A lapsed school cannot create a new locale." This one ADDS to an enforcement
 * path that already existed rather than replacing it: `get_epack_quota` already
 * refuses an expired school (its balances sum to 0), but answers `99999` for a
 * school that has never subscribed, because `_school_has_any_subscription` is
 * false and enforcement was never switched on for it. That is the case this
 * closes, so it must be checked BEFORE the quota call, not after it.
 */
export const canCreateLocale = canWriteLocale;

export default canWriteLocale;
